import { useState, type ReactNode } from 'react';
import type { TrainingBlock } from '@cadencia/contracts';
import { Boton, Tarjeta } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { rescheduleBlock } from '../../data/services/rescheduleBlockService.js';
import { formatLongDate } from '../../lib/dates.js';
import { CuandoEmpezar, lunesPorDefecto } from './CuandoEmpezar.js';
import { mensajeDeError } from '../../lib/errores.js';

/**
 * Cambiar el día en el que empieza el bloque.
 *
 * La fecha se elige al CREAR el bloque, y esa tarjeta solo aparece cuando no
 * hay ninguno activo. Un bloque creado para probar la aplicación se queda ahí
 * corriendo: pasan las semanas, la cabecera dice «semana 3 de 4» sin haber
 * entrenado nada, y no había forma de volver atrás.
 *
 * POR QUÉ EL CONTROL SE OFRECE INCLUSO CUANDO NO SE PUEDE MOVER
 *
 * Con entrenamiento registrado dentro no se mueve, y esconder el botón por eso
 * dejaría al usuario buscando dónde se toca una fecha que sabe que existe. Lo
 * que se esconde es el formulario: el botón sigue ahí y al abrirlo explica el
 * motivo. Al revés —el motivo pintado siempre bajo la cabecera— convierte una
 * regla que se cumple casi todo el tiempo en un aviso permanente en la
 * pantalla que más se usa.
 *
 * El motivo, además, no lo redacta esta pantalla: los códigos vienen de
 * `canRescheduleBlock`, que es quien decide, y aquí solo se traducen.
 */

const MOTIVOS: Record<string, string> = {
  block_has_logged_work:
    'Ya hay entrenamiento registrado en este bloque, así que sus fechas no se mueven: separarlas de los días en que entrenaste falsearía el historial. Este bloque se termina o se cierra, no se muda.',
  start_in_past:
    'No se puede empezar antes de esta semana: fabricaría sesiones perdidas que nadie perdió.',
  same_start: 'El bloque ya empieza ese día.',
};

function traducir(codigos: string[]): string {
  return codigos.map((codigo) => MOTIVOS[codigo] ?? codigo).join(' ');
}

export function CambiarInicio({
  bloque,
  puedeMoverse,
}: {
  bloque: TrainingBlock;
  /** `false` si el bloque ya tiene entrenamiento dentro. */
  puedeMoverse: boolean;
}): ReactNode {
  const { ctx } = useAppData();
  const [abierto, setAbierto] = useState(false);
  const [fecha, setFecha] = useState(() => lunesPorDefecto());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = (): void => {
    if (ctx === null) return;
    setGuardando(true);
    setError(null);
    void (async () => {
      try {
        const salida = await rescheduleBlock(ctx, bloque, fecha);
        if (!salida.moved) {
          setError(traducir(salida.reasonCodes));
          return;
        }
        setAbierto(false);
      } catch (caught) {
        setError(mensajeDeError(caught));
      } finally {
        setGuardando(false);
      }
    })();
  };

  if (!abierto) {
    return (
      <Boton variante="texto" onClick={() => setAbierto(true)}>
        Cambiar cuándo empieza
      </Boton>
    );
  }

  if (!puedeMoverse) {
    return (
      <Tarjeta
        titulo="Este bloque ya no se mueve"
        acciones={<Boton variante="secundario" onClick={() => setAbierto(false)}>Entendido</Boton>}
      >
        <p className="campo__pista">{traducir(['block_has_logged_work'])}</p>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta
      titulo="Cambiar cuándo empieza el bloque"
      descripcion={`Ahora empieza el ${formatLongDate(bloque.startedOn)}. Se mueven con él sus semanas, sus sesiones y el cardio.`}
      acciones={
        <>
          <Boton onClick={guardar} disabled={guardando || fecha === ''}>
            {guardando ? 'Moviendo…' : 'Mover el bloque'}
          </Boton>
          <Boton variante="secundario" onClick={() => setAbierto(false)} disabled={guardando}>
            Dejarlo como está
          </Boton>
        </>
      }
    >
      <CuandoEmpezar valor={fecha} onChange={setFecha} deshabilitado={guardando} />
      {error !== null && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
    </Tarjeta>
  );
}
